import React, { useState } from 'react';
import styled from 'styled-components';
import { Card } from './StyledComponents';

// ---------------------------------------------------------------------------
// FAQ — the questions people actually ask, answered plainly, including the
// uncomfortable ones. Static content by design: no wallet, no chain reads.
// Live figures belong on the Overview and System tabs.
// ---------------------------------------------------------------------------

const Wrap = styled.div`
  grid-column: 1 / -1;
  display: flex; flex-direction: column; gap: 1rem;
`;

const Intro = styled.div`
  text-align: center; padding: 0.5rem 0 0;
  h2 { color: #ffd700; margin: 0 0 0.4rem; }
  p { color: #999; max-width: 640px; margin: 0 auto; font-size: 0.9rem; line-height: 1.5; }
`;

const Group = styled.div`
  h3 { color: #ffd700; font-size: 1.02rem; margin: 0 0 0.6rem; }
`;

const Item = styled.div`
  border-top: 1px solid #242424;
  &:first-of-type { border-top: none; }

  button {
    width: 100%; text-align: left; background: none; border: none; cursor: pointer;
    color: #eee; font-size: 0.92rem; font-weight: 600; padding: 0.85rem 0;
    display: flex; justify-content: space-between; gap: 1rem;
    &:hover { color: #ffd700; }
    .chev { color: #666; flex-shrink: 0; }
  }
  .a {
    color: #aaa; font-size: 0.87rem; line-height: 1.65; padding: 0 0 1rem;
    max-width: 78ch;
    strong { color: #ddd; }
  }
`;

const GROUPS = [
  {
    title: 'Borrowing',
    items: [
      ['What is JDAI, in one sentence?',
        <>A token you mint by locking PLS as collateral, which targets the USD value of <strong>1/1000 of an ounce of gold</strong>. You borrow it against your PLS and repay whenever you like to unlock that PLS.</>],
      ['Why gold instead of a dollar peg?',
        <>A dollar target only tells you how the dollar is doing. Gold is a unit of account older than any currency, so a gold-denominated loan keeps its meaning across long stretches of monetary history. It also means JDAI is <strong>not</strong> a stablecoin in the dollar sense — its target moves with the gold price, which is why it is called an unstablecoin.</>],
      ['Is there a repayment schedule?',
        <>No. No due date, no minimum payment, no penalty for holding a loan open. The only thing that matters is staying above the collateral minimum. A <strong>stability fee</strong> accrues continuously on your debt, and your vault page shows what that costs you per year.</>],
      ['What is the smallest vault I can open?',
        <>The debt floor is <strong>1 JDAI</strong>. A floor exists so that liquidations stay economically meaningful — processing one has to be worth somebody&apos;s gas.</>],
      ['What does the safety number mean?',
        <><strong>1.00x is the line.</strong> Above it you are fine. At or below it you cannot borrow or withdraw, and the vault becomes eligible for liquidation once that system is switched on. 1.00x means you hold exactly the minimum collateral ratio, so 1.13x means you hold 13% more collateral than the bare minimum.</>]
    ]
  },
  {
    title: 'Liquidation',
    items: [
      ['Can my collateral actually be seized?',
        <><strong>Yes.</strong> The liquidation system is deployed and tested. It is currently switched off — every liquidation ceiling is set to zero, so no vault can be liquidated today — and it will be activated in stages with <strong>advance notice</strong>. Do not open a position assuming liquidation will never happen.</>],
      ['What exactly happens if I get liquidated?',
        <>Your collateral is taken into a public auction and your debt is cancelled, with a <strong>13% penalty</strong> added to what must be recovered. Buyers bid JDAI for your collateral on a falling price. Once enough is raised to cover the debt and penalty the auction stops — and <strong>any collateral left over comes back to you.</strong> You keep the JDAI you originally borrowed.</>],
      ['So I might have collateral waiting for me afterwards?',
        <>Very likely — liquidations rarely need all of it. That leftover is yours permanently: <strong>it never expires and nobody else can take it.</strong> Connect your wallet and the bell icon in the header will tell you if anything is waiting, with a one-click withdrawal back to native PLS.</>],
      ['How do I avoid it?',
        <>Keep the safety number comfortably above 1.00x, by adding collateral or repaying some debt. Your vault page shows the exact PLS price at which you reach the line, and what your safety becomes if PLS falls 10%, 25%, or 50%. Both fixes take effect immediately, and there is no deadline as long as you act before a liquidation is triggered.</>],
      ['Who decides to liquidate me?',
        <>Nobody decides — the contracts do, based purely on your collateral ratio. Triggering a liquidation is a permissionless public function and whoever calls it earns a small bounty from the system. In practice an automated keeper watches every vault continuously, but it has no special authority.</>]
    ]
  },
  {
    title: 'Auctions and TKR',
    items: [
      ['How do I bid in an auction?',
        <>Open the <strong>Auctions</strong> tab. Collateral auctions are Dutch: the price falls over time and the first buyer to accept it wins, so there is no bidding war — just a judgement about when the price is good. You pay from an internal JDAI balance (deposited in one click on that page) and the collateral arrives in your wallet as native PLS.</>],
      ['What is TKR for?',
        <>TKR is the system&apos;s equity. When the system takes a loss its surplus cannot cover, it <strong>mints new TKR</strong> and sells it to raise the shortfall, diluting existing holders. When the system is profitable, it uses the surplus to <strong>buy TKR and destroy it</strong>, so remaining holders own more of the system. Total supply is a running record of lifetime performance.</>],
      ['Does that mean JDAI holders absorb losses?',
        <>No, and that is the point of the design: losses land on TKR holders through dilution so that JDAI stays fully backed. This has been exercised live on this deployment — a real shortfall was covered by a real TKR mint, and real system profit was distributed by a real TKR burn.</>]
    ]
  },
  {
    title: 'Risk and trust',
    items: [
      ['Who controls the system?',
        <>Today a <strong>single administrative key</strong> sets parameters such as prices, ceilings, and fees. That is a genuine risk, stated plainly rather than buried: a compromise of that key could damage the system. Migration to multi-signature control is planned before liquidations are activated for general use. The <strong>System</strong> and <strong>Contracts</strong> tabs show every parameter and every configuration change ever made.</>],
      ['Are the contracts audited?',
        <>The contracts are MakerDAO&apos;s, reused unchanged — deployed byte-for-byte identical to the audited Ethereum versions (only compiler metadata differs), and each is source-verified so anyone can check. That covers the contract logic. It does <strong>not</strong> mean this deployment&apos;s configuration, oracle, or operational practices inherit those audits; those are this project&apos;s own responsibility.</>],
      ['What if the price oracle fails or lies?',
        <>The oracle is a small number of authorized signers posting the PLS price, with the gold target derived alongside it. That is a trust assumption: a wrong price could wrongly liquidate vaults or allow over-borrowing. The <strong>System</strong> tab shows the last update time and how many signatures are required, so staleness is visible rather than hidden.</>],
      ['How small is this really?',
        <>Very. Total debt is a few dozen JDAI and the markets for both JDAI and TKR are extremely thin, so prices can move sharply and you may not be able to trade in size. Treat this as an early-stage system that works and is honestly reported — not as deep infrastructure.</>],
      ['Is there an emergency shutdown?',
        <>Not yet. MakerDAO&apos;s global settlement contract is not deployed here, so there is no built-in orderly wind-down. If something went badly wrong the response would be manual parameter changes by the administrator. This is a known gap, listed here rather than omitted.</>]
    ]
  }
];

const FaqPage = () => {
  const [open, setOpen] = useState('0-0');
  return (
    <Wrap>
      <Intro>
        <h2>Questions</h2>
        <p>
          Straight answers, including the uncomfortable ones. Live figures are on the
          Overview and System tabs; contract addresses and every configuration change
          ever made are on Contracts.
        </p>
      </Intro>
      {GROUPS.map((g, gi) => (
        <Card key={g.title}>
          <Group>
            <h3>{g.title}</h3>
            {g.items.map(([q, a], ii) => {
              const id = `${gi}-${ii}`;
              const isOpen = open === id;
              return (
                <Item key={id}>
                  <button onClick={() => setOpen(isOpen ? null : id)}>
                    <span>{q}</span>
                    <span className="chev">{isOpen ? '−' : '+'}</span>
                  </button>
                  {isOpen && <div className="a">{a}</div>}
                </Item>
              );
            })}
          </Group>
        </Card>
      ))}
    </Wrap>
  );
};

export default FaqPage;
